/*
 * Tracking Calendar Options service
 * Stores and loads user preferences for the tracking calendar
 *
 * Angular module: "trackerbase-app.services.tracking-calendar-options-service"
 * Service Name: TrackingCalendarOptions
 * 
 */

import angular from "angular";

import Assert from "tbAssert";

import UserInfoServiceModule from "./UserInfoService";

const unknownUserPrefsKey = "unknownUser75";
const defaultDaysToShowInAgendaView = 7;
const defaultCalendarDefaultView = "agenda";

const allowedCalendarViews = new Map([
    ["agenda", "Agenda"],
    ["day", "Day"],
    ["threeday","3 Day"],
    ["week", "Week"],
    ["month", "Month"]
]);

const mCanStorePrefs = new WeakMap();
const mUserPrefsKey = new WeakMap();

const agendaDaysViewKey = "agendaViewDays";
const mDaysToShowInAgendaView = new WeakMap();

const defaultCalendarViewKey = "defaultCalendarView";
const mDefaultCalendarView = new WeakMap();

const mUserInfoSvc = new WeakMap();
const mLogSvc = new WeakMap();
const mStoreSvc = new WeakMap();

class TrackingCalendarOptionsService {
    constructor($log, store, UserInfoService) {

        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");
        Assert.StrictAssert(store != null, "Assertion Failure: Missing Storage Service Dependency");
        Assert.StrictAssert(UserInfoService != null, "Assertion Failure: Missing User Info Service Dependency");

        mLogSvc.set(this, $log);
        mStoreSvc.set(this, store);
        mUserInfoSvc.set(this, UserInfoService);

        mCanStorePrefs.set(this, false);
        mUserPrefsKey.set(this, unknownUserPrefsKey);

        mDaysToShowInAgendaView.set(this, defaultDaysToShowInAgendaView);

        mDefaultCalendarView.set(this, defaultCalendarDefaultView);

        this._loadPrefs();
        mLogSvc.get(this).debug("Tracking Calendar Options Service Initialized.");
        mLogSvc.get(this).debug(this);
    }

    _buildFullKey(itemKey) {
        return `${itemKey}.${mUserPrefsKey.get(this)}`;
    }

    _loadPrefs() {
        let userName = mUserInfoSvc.get(this).userName;

        if (userName == null) {
            mUserPrefsKey.set(this, unknownUserPrefsKey);
            mCanStorePrefs.set(this, false);
            return;
        }

        mUserPrefsKey.set(this, `${userName}.userPrefs`);
        mCanStorePrefs.set(this, true);

        let agendaViewsPrefFullKey = this._buildFullKey(agendaDaysViewKey);
        let storedDaysToShowInAgendaView = mStoreSvc.get(this).get(agendaViewsPrefFullKey);
        if ((Number.isInteger(storedDaysToShowInAgendaView)) &&
            (storedDaysToShowInAgendaView <= 7) &&
            (storedDaysToShowInAgendaView > 0))
        {
            mDaysToShowInAgendaView.set(this, storedDaysToShowInAgendaView);
        }
        else {
            mDaysToShowInAgendaView.set(this, defaultDaysToShowInAgendaView);
            mStoreSvc.get(this).set(agendaViewsPrefFullKey, mDaysToShowInAgendaView.get(this));
        }

        let defaultCalendarViewFullKey = this._buildFullKey(defaultCalendarViewKey);
        let storedDefaultCalendarView = mStoreSvc.get(this).get(defaultCalendarViewFullKey);
        if (allowedCalendarViews.has(storedDefaultCalendarView)) {

            mDefaultCalendarView.set(this, storedDefaultCalendarView);
        }
        else {
            mDefaultCalendarView.set(this, defaultCalendarDefaultView);
            mStoreSvc.get(this).set(defaultCalendarViewFullKey, mDefaultCalendarView.get(this));
        }
    }

    toString() {
        return "TrackingCalendarOptionsService";
    }

    loadPrefs() {
        this._loadPrefs();
    }

    get AgendaDaysToShowPref() {
        let daysToShowInAgendaView = mDaysToShowInAgendaView.get(this);
        mLogSvc.get(this).debug(`Agenda View Days Property Read. Value is ${daysToShowInAgendaView}`);
        return daysToShowInAgendaView;
    }

    set AgendaDaysToShowPref(numDays) {
        if ((Number.isInteger(numDays)) && (numDays > 0) && (numDays <= 7)) {
            mDaysToShowInAgendaView.set(this, numDays);
            if (mCanStorePrefs.get(this) === true) {
                let agendaViewsPrefFullKey = this._buildFullKey(agendaDaysViewKey);
                mStoreSvc.get(this).set(agendaViewsPrefFullKey, mDaysToShowInAgendaView.get(this));
                mLogSvc.get(this).debug(`New Agenda View Days Property Stored: ${mDaysToShowInAgendaView.get(this)}`);
            }
            else {
                mLogSvc.get(this).debug(`Agenda View Days Pref Set To ${mDaysToShowInAgendaView.get(this)}, But Could Not Store`);
            }
        }
        else {
            mLogSvc.get(this).debug("Agenda View Days Property Setter: Not Changed Due To Invalid Value");
        }
    }

    get DefaultCalendarView() {
        let currentDefaultCalendarView = mDefaultCalendarView.get(this);
        mLogSvc.get(this).debug(`Default Calendar View Property Read. Value is ${currentDefaultCalendarView}`);
        return currentDefaultCalendarView;
    }

    set DefaultCalendarView(calendarView) {
        if (allowedCalendarViews.has(calendarView)) {
            mDefaultCalendarView.set(this, calendarView);
            if (mCanStorePrefs.get(this) === true) {
                let defaultCalendarViewFullKey = this._buildFullKey(defaultCalendarViewKey);
                mStoreSvc.get(this).set(defaultCalendarViewFullKey, mDefaultCalendarView.get(this));
                mLogSvc.get(this).debug(`Set and stored default calendar view: ${mDefaultCalendarView.get(this)}`);
            }
            else {
                mLogSvc.get(this).debug(`Set, but did not store, default calendar view: ${mDefaultCalendarView.get(this)}`);
            }
        }
        else {
            mLogSvc.get(this).debug("Did not set new default calendar view: invalid value.");
        }
    }

    get AvailableCalendarViews() {
        return Array.from(allowedCalendarViews.entries());
    }

    GetCalendarViewDisplayName(calendarViewName) {
        return angular.copy(allowedCalendarViews.get(calendarViewName));
    }
}

TrackingCalendarOptionsService.$inject = [ "$log", "store", "UserInfoService" ];

export default angular
    .module("trackerbase-app.services.tracking-calendar-options-service", [
        "angular-storage",
        UserInfoServiceModule
    ])
    .service("TrackingCalendarOptions", TrackingCalendarOptionsService)
    .name;



//////////////////
// WEBPACK FOOTER
// ./src/services/TrackingCalendarOptionsService.js
// module id = dmk7
// module chunks = 1